import { MICROPHONE_AUDIO_PROFILE } from '@/shared/config';
import { mediaStreamTracker } from '@/shared/utils/mediaStreamTracker';

import { debug } from '../debug';

type MicrophoneConstraints = {
  deviceId?: string;
};

class MicrophoneController {
  private stream: MediaStream | null = null;
  private streamId: string | null = null;
  private currentDeviceId: string | null = null;

  async open(constraints: MicrophoneConstraints = {}): Promise<MediaStream> {
    const targetDeviceId = constraints.deviceId;

    if (this.stream && this.currentDeviceId === targetDeviceId) {
      return this.stream;
    }

    await this.close();

    const mediaConstraints: MediaStreamConstraints = {
      audio: targetDeviceId
        ? { ...MICROPHONE_AUDIO_PROFILE, deviceId: { exact: targetDeviceId } }
        : { ...MICROPHONE_AUDIO_PROFILE },
    };

    const stream = await navigator.mediaDevices.getUserMedia(mediaConstraints);

    debug(
      '[microphoneController] track settings:',
      stream.getAudioTracks()[0]?.getSettings(),
    );
    const streamId = mediaStreamTracker.addStream(
      stream,
      'microphoneController',
    );

    this.stream = stream;
    this.streamId = streamId;
    this.currentDeviceId = MicrophoneController.extractDeviceId(stream);

    return stream;
  }

  getStream(): MediaStream | null {
    if (this.stream && this.isStreamActive()) {
      return this.stream;
    }

    if (this.stream) {
      this.close();
    }

    return null;
  }

  private isStreamActive(): boolean {
    if (!this.stream) {
      return false;
    }

    return this.stream.getTracks().some((track) => track.readyState === 'live');
  }

  getCurrentDeviceId(): string | null {
    return this.currentDeviceId;
  }

  async close(): Promise<void> {
    if (!this.stream) {
      return;
    }

    this.stream.getTracks().forEach((track) => {
      if (track.readyState === 'live') {
        track.stop();
      }
    });

    if (this.streamId) {
      mediaStreamTracker.removeStream(this.streamId);
    }

    this.stream = null;
    this.streamId = null;
    this.currentDeviceId = null;
  }

  private static extractDeviceId(stream: MediaStream): string | null {
    const [track] = stream.getAudioTracks();
    return track?.getSettings().deviceId ?? null;
  }
}

export const microphoneController = new MicrophoneController();
